import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../../hooks/usePermissions';
import { taskPageUrl } from '../utils/taskPageUrl';
import { isLowOrMediumPriority } from '../utils/priorityDisplay';
import type { WorkIdRecord } from '../workIdService';
import type { WorkIdActionsMenuItem } from '../components/WorkIdActionsMenu';

interface WorkIdActionCallbacks {
  onEdit?: (record: WorkIdRecord) => void;
  onApprove?: (record: WorkIdRecord) => void;
  onCancel?: (record: WorkIdRecord) => void;
  onReopen?: (record: WorkIdRecord) => void;
  onReopenTask?: (record: WorkIdRecord) => void;
  onAssignExisting?: (record: WorkIdRecord) => void;
  onCreateNewTask?: (record: WorkIdRecord) => void;
  onUnassign?: (record: WorkIdRecord) => void;
  onMerge?: (record: WorkIdRecord) => void;
  onViewMergeSuggestions?: (record: WorkIdRecord) => void;
  onUnmerge?: (record: WorkIdRecord) => void;
  onReviewHistory?: (record: WorkIdRecord) => void;
  onExportToPdf?: (record: WorkIdRecord) => void;
  onReportAccomplishment?: (record: WorkIdRecord) => void;
  onCloseTask?: (record: WorkIdRecord) => void;
  onCloseWithoutAction?: (record: WorkIdRecord) => void;
}

/**
 * Build action menu items for a Work ID (shared by table actions dropdown and map right-click menu).
 */
export function useWorkIdActionItems(
  record: WorkIdRecord,
  callbacks: WorkIdActionCallbacks,
  exportingId: number | null = null
): WorkIdActionsMenuItem[] {
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const hasWorkIdPerm = (key: string) => hasPermission(`workId:${key}`);

  return useMemo(() => {
    const canApprove = hasWorkIdPerm('approve');
    const canCancel = hasWorkIdPerm('cancel');
    const canAssign = hasWorkIdPerm('assign');
    const canMerge = hasWorkIdPerm('merge');
    const canCloseWithoutAction = hasWorkIdPerm('close_without_action');
    const canEdit = hasWorkIdPerm('create') || hasWorkIdPerm('approve');
    const canReviewHistory = hasWorkIdPerm('review_history');
    const canExport = hasWorkIdPerm('view');

    const status = record.status?.toUpperCase() || '';
    const showApprove = canApprove && status === 'NEW';
    const showCancel = canCancel && status !== 'CANCELLED';
    const showReopen =
      canCancel && (status === 'CANCELLED' || status === 'CLOSED_WITHOUT_ACTION');
    const showReopenTask = canApprove && status === 'CLOSED';
    const showAssign = canAssign && status === 'APPROVED';
    const showUnassign = canAssign && status === 'ASSIGNED' && (record.tasks?.length ?? 0) > 0;
    const showMerge = canMerge && (status === 'NEW' || status === 'APPROVED' || status === 'ASSIGNED');
    const showUnmerge = canMerge && status === 'MERGED';

    const linkedTask = record.tasks?.length === 1 ? record.tasks[0] : null;
    const taskStatus = linkedTask?.status?.toUpperCase() ?? '';
    const showReportAccomplishment =
      status === 'ASSIGNED' &&
      linkedTask?.type === 'Quantified' &&
      (taskStatus === 'ACTIVE' || taskStatus === 'ACTIVE_MARKED_AS_COMPLETE') &&
      hasPermission('quantified:accomplishment_submit') &&
      !!callbacks.onReportAccomplishment;
    const showCloseTask =
      status === 'ASSIGNED' &&
      linkedTask?.type === 'Routine' &&
      taskStatus === 'ACTIVE' &&
      canAssign &&
      !!callbacks.onCloseTask;
    const showCloseWithoutAction =
      canCloseWithoutAction &&
      (status === 'NEW' || status === 'APPROVED') &&
      isLowOrMediumPriority(record.priority) &&
      !!callbacks.onCloseWithoutAction;

    const items: WorkIdActionsMenuItem[] = [];

    if (canEdit && callbacks.onEdit) {
      items.push({ label: 'Edit', onClick: () => callbacks.onEdit!(record) });
    }
    if (showApprove && callbacks.onApprove) {
      items.push({ label: 'Approve', onClick: () => callbacks.onApprove!(record) });
    }
    if (showCancel && callbacks.onCancel) {
      items.push({ label: 'Cancel', onClick: () => callbacks.onCancel!(record) });
    }
    if (showCloseWithoutAction && callbacks.onCloseWithoutAction) {
      items.push({
        label: 'Close without action',
        onClick: () => callbacks.onCloseWithoutAction!(record),
      });
    }
    if (showReopen && callbacks.onReopen) {
      items.push({ label: 'Reopen', onClick: () => callbacks.onReopen!(record) });
    }
    if (showReopenTask && callbacks.onReopenTask) {
      items.push({ label: 'Reopen task', onClick: () => callbacks.onReopenTask!(record) });
    }
    if (showReportAccomplishment && callbacks.onReportAccomplishment) {
      items.push({ label: 'Report Accomplishment', onClick: () => callbacks.onReportAccomplishment!(record) });
    }
    if (showCloseTask && callbacks.onCloseTask) {
      items.push({ label: 'Close', onClick: () => callbacks.onCloseTask!(record) });
    }
    if (status === 'ASSIGNED' && linkedTask && (linkedTask.type === 'Routine' || linkedTask.type === 'Quantified')) {
      const label = linkedTask.type === 'Routine' ? 'Go to Routine Work' : 'Go to Quantified Work';
      items.push({
        label,
        onClick: () => navigate(taskPageUrl(linkedTask, true)),
      });
    }
    if (showAssign && callbacks.onAssignExisting) {
      items.push({ label: 'Assign to Existing Task', onClick: () => callbacks.onAssignExisting!(record) });
    }
    if (showAssign && callbacks.onCreateNewTask) {
      items.push({ label: 'Create New Task and Assign', onClick: () => callbacks.onCreateNewTask!(record) });
    }
    if (showUnassign && callbacks.onUnassign) {
      items.push({ label: 'Remove from Task', onClick: () => callbacks.onUnassign!(record) });
    }
    if (record.hasMergeSuggestions && status !== 'MERGED' && callbacks.onViewMergeSuggestions) {
      items.push({ label: 'View merge suggestions', onClick: () => callbacks.onViewMergeSuggestions!(record) });
    }
    if (showMerge && callbacks.onMerge) {
      items.push({ label: 'Merge', onClick: () => callbacks.onMerge!(record) });
    }
    if (showUnmerge && callbacks.onUnmerge) {
      items.push({ label: 'Unmerge', onClick: () => callbacks.onUnmerge!(record) });
    }
    if (canReviewHistory && callbacks.onReviewHistory) {
      items.push({ label: 'Review History', onClick: () => callbacks.onReviewHistory!(record) });
    }
    if (canExport && callbacks.onExportToPdf) {
      items.push({
        label: exportingId === record.id ? 'Exporting...' : 'Export to PDF',
        onClick: () => callbacks.onExportToPdf!(record),
        disabled: exportingId === record.id
      });
    }

    return items;
  }, [
    record,
    exportingId,
    navigate,
    callbacks.onEdit,
    callbacks.onApprove,
    callbacks.onCancel,
    callbacks.onReopen,
    callbacks.onReopenTask,
    callbacks.onAssignExisting,
    callbacks.onCreateNewTask,
    callbacks.onUnassign,
    callbacks.onMerge,
    callbacks.onViewMergeSuggestions,
    callbacks.onUnmerge,
    callbacks.onReviewHistory,
    callbacks.onExportToPdf,
    callbacks.onReportAccomplishment,
    callbacks.onCloseTask,
    callbacks.onCloseWithoutAction
  ]);
}
